import { Catalog } from '@df/catalog/catalog.interfaces';
import { Model, TLoadParams } from '@df/catalog/model';
import compact from 'lodash-es/compact';
import defaults from 'lodash-es/defaults';
import isArray from 'lodash-es/isArray';
import isNumber from 'lodash-es/isNumber';
import isString from 'lodash-es/isString';
import reduce from 'lodash-es/reduce';

/**
 * Attribute model representing magento attributes
 */
export class Attribute extends Model<Catalog.IAttributeData, string> {
    /**
     * Object type id
     * @type {String}
     */
    static override type = 'attribute';

    groups?: Catalog.IAttributeOptionGroup[];

    override async load(verbosity: number, params: Partial<TLoadParams> = {}): Promise<void> {
        if (this.canLoadDirectly()) {
            super.load(verbosity, params);
        }
    }

    override isLoadNeeded(verbosity: number, params: TLoadParams): boolean {
        return this.canLoadDirectly() && super.isLoadNeeded(verbosity, params);
    }

    /**
     * We can only load attributes directly if not init setyp is applied
     */
    private canLoadDirectly(): boolean {
        return !!this.app.dfConfig.integration.noInit;
    }

    /**
     * Returs option label by option value
     * @param {number | number[]} option value
     * @return {string}
     */
    getOptionLabel(
        value: number | string | number[],
        options: undefined | Catalog.IAttributeOptionGroup[] | Catalog.IAttributeOption[] = this.data?.options
    ): string | undefined | string[] | number | number[] {
        if (options) {
            if (isArray(value)) {
                return <string[]>compact((<number[]>value).map(v => this.getOptionLabel(v, options)));
            }
            if (isNumber(value)) {
                const option = options.find(i => i.value === value);
                if (option) {
                    return option.label;
                }
            }
        }
        return value;
    }
    /**
     * Returs option value by option label
     * @param {string | string[]} option value
     * @return {number}
     */
    getOptionValue(
        label: number | string | (number | string)[],
        options: undefined | Catalog.IAttributeOptionGroup[] | Catalog.IAttributeOption[] = this.data?.options
    ): number | number[] | undefined {
        if (options) {
            if (isArray(label)) {
                return <number[]>compact((<string[]>label).map(v => this.getOptionValue(v, options)));
            }
            if (isString(label) || isNumber(label)) {
                /**
                 * It is necessary to use '==' here instead of '==='
                 * because the 'label' can be either a string or a number.
                 */
                const option = options.find(i => i.label == label);
                if (option) {
                    return option.value;
                }
            }
        }
        return undefined;
    }

    /**
     * Returs swatch option color hash by option value
     * @param {number} option value
     * @return {string}
     */
    getOptionColor(value: number): string | undefined {
        if (this.data?.options) {
            const option = this.data.options.find(i => i.value === value);

            if (option && option.hasColor) {
                return option.swatch;
            }
        }

        return undefined;
    }

    /**
     * Returs swatch option image src by option value
     * @param {number} option value
     * @return {string}
     */
    getOptionImage(value: number): string | undefined {
        if (this.data?.options) {
            const option = this.data.options.find(i => i.value === value);

            if (option && option.hasImage) {
                return option.swatch;
            }
        }

        return undefined;
    }

    /**
     * Gets attribute options groups
     * This is used on listing when we want to reduce items listed
     *
     * @return {IAttributeOptionGroup[]}
     */
    getGroups(): Catalog.IAttributeOptionGroup[] {
        if (!this.groups) {
            const options = <Catalog.IAttributeOption[]>this.data?.options || [];
            this.groups = Object.values(
                reduce(
                    options,
                    (memo: { [key: string]: Catalog.IAttributeOptionGroup }, option: Catalog.IAttributeOption, index: number) => {
                        const groupName = option.group || option.label;
                        if (!memo[groupName]) {
                            const group: Catalog.IAttributeOptionGroup = defaults(
                                {
                                    label: groupName,
                                    values: [],
                                    position: index
                                },
                                option
                            );
                            memo[groupName] = group;
                        }
                        memo[groupName].values.push(option.value);
                        return memo;
                    },
                    <any>{}
                )
            );
        }
        return this.groups;
    }

    getOptionByValue(value: number): Catalog.IAttributeOption | undefined {
        if (this.data?.options?.length) {
            return this.data.options.find(option => option.value === value);
        }
        return undefined;
    }

    /**
     * Readable name getter
     */
    override get name() {
        return this.data?.label;
    }
}
